import { z } from 'zod';

import { TEST_CONSTANTS } from '../constants';
import { CEFRLevelEnum, EducationEnum, GenderEnum, OccupationEnum } from '../services/enums';

export const VocabTestStatusSchema = z.enum(['new', 'in-progress', 'completed', 'discarded']);
export type AdaptiveVocabTestStatus = z.infer<typeof VocabTestStatusSchema>;

export const VocabTestModeSchema = z.enum(['sentences', 'words', 'hybrid', 'batch_words']);
export type VocabTestMode = z.infer<typeof VocabTestModeSchema>;

export const UserCefrTestSchema = z.object({
  test_id: z.string().optional().nullable(),
  test_date: z.string().optional().nullable(),
  cefr_level: z.enum(CEFRLevelEnum).optional().nullable(),
  reading_score: z.enum(CEFRLevelEnum).optional().nullable(),
  writing_score: z.enum(CEFRLevelEnum).optional().nullable(),
  listening_score: z.enum(CEFRLevelEnum).optional().nullable(),
  speaking_score: z.enum(CEFRLevelEnum).optional().nullable(),
  raw_test_scores: z.string().optional().nullable(),
});
export type AdaptiveUserCefrTest = z.infer<typeof UserCefrTestSchema>;

export const UserProfileSchema = z.object({
  native_language: z.string().optional().nullable(),
  age_group: z.number().optional().nullable(),
  country_of_residence: z.string().optional().nullable(),
  email: z.string().optional().nullable(),
  education_level: z.enum(EducationEnum).optional().nullable(),
  occupation: z.enum(OccupationEnum).optional().nullable(),
  gender: z.enum(GenderEnum).optional().nullable(),
  full_name: z.string().optional().nullable(),
  cefr_test: UserCefrTestSchema.optional().nullable(),
  email_opt_in: z.boolean().optional().nullable(),
  schema_version: z.number().optional().nullable(),
});
export type AdaptiveUserProfile = z.infer<typeof UserProfileSchema>;

export const GENIUS_LEMMAS_DEFAULT = 65_000;
export const GENIUS_PHRASAL_VERBS_DEFAULT = 6_000;
export const GENIUS_IDIOMS_DEFAULT = 6_100;
export const MAX_LEMMAS_DEFAULT = 87_500;
export const MAX_PHRASAL_VERBS_DEFAULT = 6_500;
export const MAX_IDIOMS_DEFAULT = 6_750;

export const VocabularySizeSchema = z.object({
  level: z.string(),
  lemmas_number: z.number(),
  level_description: z.string().optional().nullable(),
  phrasal_verbs_number: z.number().optional().nullable(),
  idioms_number: z.number().optional().nullable(),
  native_speaker_lemmas: z.number(),
  native_speaker_phrasal_verbs: z.number(),
  native_speaker_idioms: z.number(),
  native_speaker_lemmas_percentage: z.number(),
  native_speaker_phrasal_verbs_percentage: z.number(),
  native_speaker_idioms_percentage: z.number(),
  genius_lemmas: z.number().optional().default(GENIUS_LEMMAS_DEFAULT),
  genius_phrasal_verbs: z.number().optional().default(GENIUS_PHRASAL_VERBS_DEFAULT),
  genius_idioms: z.number().optional().default(GENIUS_IDIOMS_DEFAULT),
  max_lemmas: z.number().optional().default(MAX_LEMMAS_DEFAULT),
  max_phrasal_verbs: z.number().optional().default(MAX_PHRASAL_VERBS_DEFAULT),
  max_idioms: z.number().optional().default(MAX_IDIOMS_DEFAULT),
});
export type AdaptiveVocabularySize = z.infer<typeof VocabularySizeSchema>;

// --- Page-based flow (vocab test version 13+) ---------------------------------
//
// A served question and a submitted answer mirror the backend models: exactly
// one of `self_reporting` / `headword_translation` is set, and the client picks
// the page layout from whichever is non-null.

export const SelfReportingQuestionSchema = z.object({
  headword: z.string(),
  sentence: z.string(),
  definition: z.string(),
  fake: z.boolean().optional(),
});

export const HeadwordTranslationQuestionSchema = z.object({
  word: z.string(),
  // ISO 639-3 code of the language to translate into: the user's native
  // language as the backend knows it. Null when it does not.
  language: z.string().nullable().optional(),
});

function exactlyOneKind(value: {
  self_reporting?: unknown;
  headword_translation?: unknown;
}): boolean {
  return (value.self_reporting == null) !== (value.headword_translation == null);
}

const EXACTLY_ONE_KIND_MESSAGE = 'exactly one of self_reporting / headword_translation must be set';

export const QuestionSchema = z
  .object({
    id: z.number(),
    in_test_index: z.number(),
    in_test_page: z.number(),
    // Here or in `self_reporting`, depending on the backend and on when a page was
    // stored in localStorage: read it with `isFakeQuestion`.
    fake: z.boolean().optional(),
    // Question tags, e.g. "idiom", shown next to the word.
    tags: z.array(z.string()).optional().default([]),
    self_reporting: SelfReportingQuestionSchema.nullable().optional(),
    headword_translation: HeadwordTranslationQuestionSchema.nullable().optional(),
  })
  .refine(exactlyOneKind, { message: EXACTLY_ONE_KIND_MESSAGE });
export type AdaptiveQuestion = z.infer<typeof QuestionSchema>;

export function isFakeQuestion(question: AdaptiveQuestion): boolean {
  return question.self_reporting?.fake ?? question.fake ?? false;
}

export type AdaptiveQuestionKind = 'self_reporting' | 'headword_translation';

export function getQuestionKind(question: AdaptiveQuestion): AdaptiveQuestionKind {
  return question.self_reporting != null ? 'self_reporting' : 'headword_translation';
}

// How the headword-translation page just submitted was graded.
export const PreviousPageResultSchema = z.object({
  question_id: z.number(),
  status: z.enum(['correct', 'wrong']),
  // A translation of the word, shown when the answer is wrong. Null for a blank
  // answer, and when the backend has none.
  correct_answer: z.string().nullable().optional(),
});
export type AdaptivePreviousPageResult = z.infer<typeof PreviousPageResultSchema>;

export const PageSchema = z.object({
  // Index of this page; equal to the `in_test_page` of every question on it. On
  // the final, empty page it is the index the next page would have had.
  in_test_page: z.number(),
  // Empty means the vocab test is over and the client should complete it.
  questions: z.array(QuestionSchema),
  // Answered self-reporting pages over the total, in [0, 1]; owned by the backend.
  progress: z.number().min(0).max(1),
  // Only on the reply to a submit of a headword-translation page. Null after a
  // self-reporting page, when the answer could not be graded, and on GET.
  previous_page_result: PreviousPageResultSchema.nullable().optional(),
});
export type AdaptivePage = z.infer<typeof PageSchema>;

export const SelfReportingAnswerSchema = z.object({
  checked: z.boolean(),
  revealed_meaning: z.boolean().default(false),
});

export const HeadwordTranslationAnswerSchema = z.object({
  translation: z.string().max(TEST_CONSTANTS.HEADWORD_TRANSLATION_MAX_LENGTH),
  // The `language` of the question as it was shown. Recorded with the answer;
  // it does not change the user's language (the profile endpoint does).
  language: z.string().nullable().optional(),
});

export const QuestionResponseSchema = z
  .object({
    in_test_index: z.number(),
    item_id: z.number(),
    decision_time: z.number(),
    self_reporting: SelfReportingAnswerSchema.nullable().optional(),
    headword_translation: HeadwordTranslationAnswerSchema.nullable().optional(),
  })
  .refine(exactlyOneKind, { message: EXACTLY_ONE_KIND_MESSAGE });
export type AdaptiveQuestionResponse = z.infer<typeof QuestionResponseSchema>;

export const PageSubmitSchema = z.object({
  // The `in_test_page` of the page being answered. The server rejects (400) a
  // submission for any page other than the one it is waiting for.
  in_test_page: z.number(),
  responses: z.array(QuestionResponseSchema),
  total_decision_time_ms: z.number(),
});
export type AdaptivePageSubmit = z.infer<typeof PageSubmitSchema>;

export const AdaptiveVocabularyTestSchema = z.object({
  test_id: z.string(),
  shortcode: z.string().nullable().optional(),
  referred_by: z.string().nullable().optional(),
  language: z.literal('en'),
  mode: z.string().default('sentences'),
  status: VocabTestStatusSchema,
  total_items_in_test: z.number(),
  client_buffer_size: z.number(),
  // Always empty for batch_words vocab tests: questions come from the page endpoints
  // (`GET/POST /{test_id}/pages/`). The envelope keeps these fields because the
  // create / get / profile endpoints are shared with the iOS client, whose flat
  // item schema cannot carry headword-translation questions. This workaround
  // can go once the endpoints are adapted to the page-based vocab test on all platforms.
  items: z.array(QuestionSchema),
  responses: z.array(QuestionResponseSchema),
  created_datetime: z.string(),
  completed_datetime: z.string().nullable().optional(),
  user_id: z.string().nullable().optional(),
  user_profile: UserProfileSchema.nullable().optional(),
  vocab_size: VocabularySizeSchema.nullable().optional(),
  schema_version: z.number().nullable().optional(),
  test_version: z.number(),
  web_test_version: z.string().nullable().optional(),
});
export type AdaptiveVocabularyTest = z.infer<typeof AdaptiveVocabularyTestSchema>;

export const VocabTestCreateInputSchema = z.object({
  language: z.literal('en'),
  existing_user_id: z.string().nullable().optional(),
  web_test_version: z.string().nullable().optional(),
  user_profile: UserProfileSchema.nullable().optional(),
  // Omitted for `batch_words` mode — the backend owns the batch size there and
  // returns it on the test response as `client_buffer_size`. Required for the
  // legacy single-question modes.
  client_buffer_size: z.number().optional(),
  test_mode: VocabTestModeSchema.optional().default('batch_words'),
});
export type VocabTestCreateInput = z.infer<typeof VocabTestCreateInputSchema>;

export const AdaptiveVocabularyTestResultSchema = z.object({
  test_id: z.string(),
  shortcode: z.string().nullable().optional(),
  status: VocabTestStatusSchema,
  vocab_size: VocabularySizeSchema,
  completed_datetime: z.string(),
});
export type AdaptiveVocabularyTestResult = z.infer<typeof AdaptiveVocabularyTestResultSchema>;

// Minimal view of the rich ConceptSchema returned by
// GET /adaptive_vocab_tests/{test_id}/concepts/ — the same endpoint the iOS
// client uses to build the vocab-test collection. Only the fields the web
// results funnel displays are parsed; the rest are ignored.
const VocabTestConceptSchema = z.object({
  id: z.string(),
  text: z.string(),
  short_description: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
});
export const VocabTestConceptsSchema = z.object({
  known: z.array(VocabTestConceptSchema),
  unknown: z.array(VocabTestConceptSchema),
  // Question tags (e.g. "idiom") the listed concepts were asked with, by concept id.
  question_tags: z.record(z.string(), z.array(z.string())).optional().default({}),
});
export type VocabTestConcepts = z.infer<typeof VocabTestConceptsSchema>;

export const SharedVocabTestResultsSchema = z.object({
  shortcode: z.string(),
  full_name: z.string(),
  test_datetime: z.string(),
  level: z.string(),
  level_description: z.string(),
  lemmas_number: z.number(),
  phrasal_verbs_number: z.number(),
  idioms_number: z.number(),
  native_speaker_lemmas: z.number(),
  native_speaker_phrasal_verbs: z.number(),
  native_speaker_idioms: z.number(),
  native_speaker_lemmas_percentage: z.number(),
  native_speaker_phrasal_verbs_percentage: z.number(),
  native_speaker_idioms_percentage: z.number(),
  genius_lemmas: z.number().optional().default(GENIUS_LEMMAS_DEFAULT),
  genius_phrasal_verbs: z.number().optional().default(GENIUS_PHRASAL_VERBS_DEFAULT),
  genius_idioms: z.number().optional().default(GENIUS_IDIOMS_DEFAULT),
  max_lemmas: z.number().optional().default(MAX_LEMMAS_DEFAULT),
  max_phrasal_verbs: z.number().optional().default(MAX_PHRASAL_VERBS_DEFAULT),
  max_idioms: z.number().optional().default(MAX_IDIOMS_DEFAULT),
});
export type AdaptiveSharedVocabTestResults = z.infer<typeof SharedVocabTestResultsSchema>;
